import { useMemo, useState, type ReactElement } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ArrowPathIcon, MagnifyingGlassIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import {
  Alert,
  Badge,
  Button,
  Card,
  DataTable,
  DescriptionList,
  EmptyState,
  PageHeader,
  SelectField,
  SkeletonRows,
  StatCard,
  TextField,
  useToast,
  type BadgeTone,
  type Column,
} from '@csa/ui';
import { formatWallClock, parseWallClock } from '@csa/shared';
import { api, ApiError } from '../../lib/api.js';
import { useTimeZone } from '../../lib/TimeZoneProvider.js';

/**
 * Audit search.
 *
 * Built around the question people actually arrive with, which is never
 * "show me all events". It is "what did this person do", "what happened to
 * this request", or "what was refused last week". So the window defaults to
 * seven days, the facet counts are shown before any filter is applied, and
 * the refused-and-failed events are one click away.
 *
 * Every search is recorded in the ledger. That is stated in the UI rather
 * than left implicit: a reviewer should know their own reads are audited,
 * because a ledger that records everything except access to itself has a
 * hole in exactly the place someone covering their tracks would look.
 */

interface AuditEvent {
  readonly seq: string;
  readonly occurredAt: string;
  readonly eventType: string;
  readonly facet: string;
  readonly actorUpn: string;
  readonly actorIp: string | null;
  readonly targetKey: string | null;
  readonly jobId: string | null;
  readonly outcome: string;
  readonly reason: string | null;
  readonly detail: unknown;
  readonly correlationId: string | null;
  readonly eventHash: string;
  readonly buildSha: string;
}

interface SearchResult {
  readonly events: readonly AuditEvent[];
  readonly nextCursor: string | null;
  readonly window: { from: string; to: string };
}

interface FacetCounts {
  readonly facets: Record<string, number>;
  readonly outcomes: Record<string, number>;
  readonly topActors: readonly { upn: string; count: number }[];
}

interface Filter {
  from: string;
  to: string;
  facets: string[];
  outcomes: string[];
  actorUpn: string;
  search: string;
}

const OUTCOME_TONES: Record<string, BadgeTone> = {
  success: 'success',
  failure: 'danger',
  denied: 'warning',
};

export function AuditPage(): ReactElement {
  const { toast } = useToast();
  const tz = useTimeZone();
  const [filter, setFilter] = useState<Filter>(() => defaultFilter());
  const [pages, setPages] = useState<SearchResult[]>([]);
  const [selected, setSelected] = useState<AuditEvent | null>(null);

  const facets = useQuery({
    queryKey: ['audit-facets', filter.from, filter.to],
    staleTime: 60_000,
    queryFn: () =>
      api.get<FacetCounts>(
        `/api/audit/facets?from=${encodeURIComponent(filter.from)}&to=${encodeURIComponent(filter.to)}`,
      ),
  });

  const search = useMutation({
    mutationFn: (cursor: string | null) =>
      api.post<SearchResult>('/api/audit/search', {
        from: filter.from,
        to: filter.to,
        ...(filter.facets.length ? { facets: filter.facets } : {}),
        ...(filter.outcomes.length ? { outcomes: filter.outcomes } : {}),
        ...(filter.actorUpn.trim() ? { actorUpn: filter.actorUpn.trim() } : {}),
        ...(filter.search.trim().length >= 2 ? { search: filter.search.trim() } : {}),
        limit: 100,
        ...(cursor ? { cursor } : {}),
      }),

    onSuccess: (result, cursor) => {
      // A cursored fetch appends; a fresh search replaces. Appending on a
      // new search would mix results from two different filters into one
      // table, which is how someone draws a conclusion from a row that was
      // never in the set they thought they were looking at.
      setPages((previous) => (cursor ? [...previous, result] : [result]));
      setSelected(null);
    },

    onError: (error: unknown) => {
      toast({
        tone: 'danger',
        title: 'Search failed',
        message: error instanceof ApiError ? error.message : String(error),
      });
    },
  });

  const events = useMemo(() => pages.flatMap((page) => page.events), [pages]);
  const nextCursor = pages.at(-1)?.nextCursor ?? null;

  const columns = useMemo<Column<AuditEvent>[]>(
    () => [
      {
        key: 'occurredAt',
        /**
         * The zone is on the header rather than repeated down every row, so
         * `formatBare` is used instead of `format`. Hovering gives the UTC
         * instant, which is the value an export would carry -- a reviewer
         * quoting a time in a finding should be able to get the
         * zone-independent one without leaving the page.
         */
        header: `When (${tz.timeZone})`,
        render: (event) => (
          <span
            className="whitespace-nowrap font-mono text-xs"
            title={tz.titleFor(event.occurredAt)}
          >
            {tz.formatBare(event.occurredAt).value}
          </span>
        ),
      },
      {
        key: 'eventType',
        header: 'Event',
        render: (event) => <span className="font-mono text-xs">{event.eventType}</span>,
      },
      {
        key: 'outcome',
        header: 'Outcome',
        render: (event) => (
          <Badge tone={OUTCOME_TONES[event.outcome] ?? 'neutral'}>{event.outcome}</Badge>
        ),
      },
      { key: 'actorUpn', header: 'Actor', render: (event) => event.actorUpn },
      {
        key: 'targetKey',
        header: 'Target',
        render: (event) => event.targetKey ?? <span className="text-subtle">—</span>,
      },
      {
        key: 'seq',
        header: 'Seq',
        // Monospaced and right-aligned: the sequence number is the thing a
        // reviewer quotes in a finding, so it has to be readable as a
        // number rather than as prose.
        render: (event) => <span className="csa-numeric font-mono text-xs">{event.seq}</span>,
      },
    ],
    [tz],
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      <PageHeader
        title="Audit log"
        description="Every action recorded, hash-chained and append-only. Your searches are recorded here too."
      />

      {facets.data ? (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <StatCard
            label="Events in window"
            value={sum(facets.data.facets).toLocaleString()}
            hint={`${filter.from.slice(0, 10)} to ${filter.to.slice(0, 10)}`}
          />
          <StatCard
            label="Refused"
            value={(facets.data.outcomes.denied ?? 0).toLocaleString()}
            tone={facets.data.outcomes.denied ? 'warning' : 'default'}
            hint="Actions blocked by a control"
            onActivate={() => setFilter((f) => ({ ...f, outcomes: ['denied'] }))}
          />
          <StatCard
            label="Failed"
            value={(facets.data.outcomes.failure ?? 0).toLocaleString()}
            tone={facets.data.outcomes.failure ? 'danger' : 'default'}
            onActivate={() => setFilter((f) => ({ ...f, outcomes: ['failure'] }))}
          />
          <StatCard
            label="Distinct actors"
            value={facets.data.topActors.length.toLocaleString()}
            hint="Top 15 by volume"
          />
        </div>
      ) : null}

      <Card className="flex flex-col gap-3 p-3">
        <div className="flex flex-wrap items-end gap-3">
          {/*
            The boundaries are read in the display zone, not the browser's.
            `datetime-local` carries no zone -- it is a wall-clock reading --
            so a field interpreted in one zone while the results below it are
            drawn in another means a user sets a boundary at 9am and gets rows
            starting at 7am, with nothing on screen to explain why.
          */}
          <TextField
            label={`From (${tz.timeZone})`}
            type="datetime-local"
            containerClassName="w-56"
            value={formatWallClock(new Date(filter.from), tz.timeZone)}
            onChange={(event) =>
              setFilter((f) => ({
                ...f,
                from: (parseWallClock(event.target.value, tz.timeZone) ?? new Date()).toISOString(),
              }))
            }
          />
          <TextField
            label={`To (${tz.timeZone})`}
            type="datetime-local"
            containerClassName="w-56"
            value={formatWallClock(new Date(filter.to), tz.timeZone)}
            onChange={(event) =>
              setFilter((f) => ({
                ...f,
                to: (parseWallClock(event.target.value, tz.timeZone) ?? new Date()).toISOString(),
              }))
            }
          />

          <SelectField
            label="Facet"
            containerClassName="w-48"
            value={filter.facets[0] ?? ''}
            placeholder="All facets"
            options={Object.entries(facets.data?.facets ?? {}).map(([facet, count]) => ({
              value: facet,
              label: `${facet} (${count.toLocaleString()})`,
            }))}
            onChange={(event) =>
              setFilter((f) => ({
                ...f,
                facets: event.target.value ? [event.target.value] : [],
              }))
            }
          />

          <SelectField
            label="Outcome"
            containerClassName="w-40"
            value={filter.outcomes[0] ?? ''}
            placeholder="Any outcome"
            options={[
              { value: 'success', label: 'Succeeded' },
              { value: 'failure', label: 'Failed' },
              { value: 'denied', label: 'Refused' },
            ]}
            onChange={(event) =>
              setFilter((f) => ({
                ...f,
                outcomes: event.target.value ? [event.target.value] : [],
              }))
            }
          />
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <TextField
            label="Actor"
            containerClassName="w-72"
            placeholder="someone@example.com"
            hint="Exact match, case-insensitive."
            value={filter.actorUpn}
            onChange={(event) => setFilter((f) => ({ ...f, actorUpn: event.target.value }))}
          />

          <TextField
            label="Contains"
            containerClassName="min-w-72 flex-1"
            placeholder="INC-4821, a procedure name, part of a statement"
            hint="Searches the reason and the event detail."
            value={filter.search}
            onChange={(event) => setFilter((f) => ({ ...f, search: event.target.value }))}
          />

          <Button
            onClick={() => search.mutate(null)}
            loading={search.isPending}
            leadingIcon={<MagnifyingGlassIcon className="size-4" aria-hidden />}
          >
            Search
          </Button>

          <Button
            variant="ghost"
            onClick={() => {
              setFilter(defaultFilter());
              setPages([]);
            }}
            leadingIcon={<ArrowPathIcon className="size-4" aria-hidden />}
          >
            Reset
          </Button>
        </div>
      </Card>

      <ChainStatus from={filter.from} to={filter.to} />

      {search.isPending && pages.length === 0 ? (
        <SkeletonRows rows={8} />
      ) : events.length === 0 ? (
        <EmptyState
          title={pages.length === 0 ? 'Run a search' : 'No events matched'}
          description={
            pages.length === 0
              ? 'Pick a window and search. The window defaults to the last seven days.'
              : 'Nothing in this window matched those filters. Widen the window or clear a filter.'
          }
        />
      ) : (
        <>
          <DataTable
            columns={columns}
            rows={events}
            rowKey={(event) => event.seq}
            onRowClick={setSelected}
            caption="Audit events, newest first"
            stickyHeader
          />

          {nextCursor ? (
            <Button
              variant="secondary"
              className="self-center"
              loading={search.isPending}
              onClick={() => search.mutate(nextCursor)}
            >
              Load more
            </Button>
          ) : (
            <p className="self-center text-xs text-subtle">
              {events.length.toLocaleString()} events — end of results
            </p>
          )}
        </>
      )}

      {selected ? <EventDetail event={selected} /> : null}
    </div>
  );
}

/**
 * Chain integrity for the window on screen.
 *
 * Shown alongside the results rather than on a separate page, because the
 * question "can I rely on what I am reading" applies to the rows in front of
 * the reviewer. A verification result on another screen is a result nobody
 * checks.
 */
function ChainStatus({ from, to }: { from: string; to: string }): ReactElement | null {
  const spanDays = (new Date(to).getTime() - new Date(from).getTime()) / 86_400_000;

  const verify = useQuery({
    queryKey: ['audit-verify', from, to],
    // The endpoint refuses more than 31 days, so the query is not issued
    // rather than issued and refused -- a red error box on a wide window
    // would read as a chain problem, which is the opposite of the truth.
    enabled: spanDays <= 31,
    staleTime: 5 * 60_000,
    retry: false,
    queryFn: () =>
      api.get<{
        intact: boolean;
        eventsChecked: number;
        failures: { kind: string; seq: string }[];
        gaps: string[];
      }>(`/api/audit/verify?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`),
  });

  if (spanDays > 31) {
    return (
      <p className="text-xs text-subtle">
        Chain verification covers up to 31 days. Narrow the window to verify this range, or export
        an evidence pack.
      </p>
    );
  }

  if (!verify.data) return null;

  if (verify.data.intact) {
    return (
      <p className="flex items-center gap-1.5 text-xs text-success-fg">
        <ShieldCheckIcon className="size-4" aria-hidden />
        Hash chain verified over {verify.data.eventsChecked.toLocaleString()} events in this window.
      </p>
    );
  }

  return (
    <Alert tone="danger" title="Hash chain verification failed">
      <p>
        {verify.data.failures.length} event
        {verify.data.failures.length === 1 ? '' : 's'} in this window do not match their recorded
        hash
        {verify.data.gaps.length > 0
          ? `, and ${verify.data.gaps.length} sequence number(s) are missing`
          : ''}
        . Treat everything in this range as unreliable and follow the chain-break runbook.
      </p>
      <p className="mt-2 font-mono text-xs">
        First affected sequence: {verify.data.failures[0]?.seq ?? verify.data.gaps[0]}
      </p>
    </Alert>
  );
}

/**
 * One event in full.
 *
 * The detail object is rendered as formatted JSON rather than a flattened
 * table. It is already redacted server-side, its shape differs per event
 * type, and a table that guessed at which fields matter would hide the one
 * that did on the day it mattered.
 */
function EventDetail({ event }: { event: AuditEvent }): ReactElement {
  const tz = useTimeZone();

  return (
    <Card className="flex flex-col gap-3 p-3">
      <DescriptionList
        items={[
          { label: 'Event', value: <code className="font-mono">{event.eventType}</code> },
          { label: 'Sequence', value: <code className="font-mono">{event.seq}</code> },
          {
            label: 'When',
            value: <span title={tz.titleFor(event.occurredAt)}>{tz.format(event.occurredAt)}</span>,
          },
          {
            label: 'When (UTC)',
            /**
             * Both, on the detail panel. This is the view somebody reads
             * when writing a finding, and the value they should quote is the
             * one that does not depend on who is reading it.
             */
            value: <code className="font-mono text-xs">{event.occurredAt}</code>,
          },
          { label: 'Actor', value: event.actorUpn },
          { label: 'Source address', value: event.actorIp ?? '—' },
          { label: 'Target', value: event.targetKey ?? '—' },
          {
            label: 'Job',
            value: event.jobId ? <code className="font-mono">{event.jobId}</code> : '—',
          },
          { label: 'Outcome', value: event.outcome },
          { label: 'Reason', value: event.reason ?? '—' },
          {
            label: 'Correlation',
            value: event.correlationId ? (
              <code className="font-mono text-xs">{event.correlationId}</code>
            ) : (
              '—'
            ),
          },
          { label: 'Build', value: <code className="font-mono text-xs">{event.buildSha}</code> },
          {
            /**
             * The hash is shown so a reviewer can quote a specific event in
             * a finding in a way that is checkable against the signed daily
             * checkpoint, without needing database access.
             */
            label: 'Event hash',
            value: <code className="break-all font-mono text-xs">{event.eventHash}</code>,
          },
        ]}
      />

      <div>
        <h3 className="mb-1 text-xs font-medium text-subtle">Detail</h3>
        <pre className="max-h-72 overflow-auto rounded-md bg-sunken p-3 font-mono text-xs text-body">
          {JSON.stringify(event.detail, null, 2)}
        </pre>
      </div>
    </Card>
  );
}

function defaultFilter(): Filter {
  const now = new Date();
  const from = new Date(now.getTime() - 7 * 86_400_000);
  return {
    from: from.toISOString(),
    to: now.toISOString(),
    facets: [],
    outcomes: [],
    actorUpn: '',
    search: '',
  };
}

function sum(counts: Record<string, number>): number {
  return Object.values(counts).reduce((total, count) => total + count, 0);
}
